import { cn, cva, NoddiIcon, type VariantProps } from 'noddi-ui-common';
import { ReactNode, useState } from 'react';
import { NoddiBasicCard } from '../Cards/NoddiBasicCard';
import { NoddiDialog, type NoddiDialogProps } from '../Overlay/NoddiDialog';

const informationBoxVariants = cva('', {
  variants: {
    size: {
      default: 'py-4',
      small: 'py-2'
    }
  },
  defaultVariants: {
    size: 'default'
  }
});

const titleVariants = cva('font-semibold text-gray-900', {
  variants: {
    size: {
      default: 'md:text-lg',
      small: 'text-sm md:text-base'
    }
  },
  defaultVariants: {
    size: 'default'
  }
});

interface NoddiInformationBoxProps extends VariantProps<typeof informationBoxVariants> {
  /** The title displayed in the information box */
  title: string;
  /** The content to display in the dialog when clicked */
  children: ReactNode;
  /** Optional dialog title (defaults to the same as title) */
  dialogTitle?: string;
  /** Dialog width (default matches NoddiDialog). */
  dialogMaxWidth?: NoddiDialogProps['maxWidth'];
  /** Optional className for the card */
  className?: string;
  /** Optional click handler for custom behavior */
  onClick?: () => void;
}

export const NoddiInformationBox = ({
  title,
  children,
  dialogTitle,
  dialogMaxWidth,
  className,
  onClick,
  size = 'default'
}: NoddiInformationBoxProps) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const handleClick = () => {
    if (onClick) {
      onClick();
    } else {
      setIsDialogOpen(true);
    }
  };

  return (
    <div>
      <NoddiBasicCard onClick={handleClick} className={cn(informationBoxVariants({ size }), className)}>
        <div className='flex items-center justify-between gap-2'>
          <div className='flex items-center gap-2'>
            <NoddiIcon name='Info' className='fill-primary text-white' size='medium' />
            <h3 className={titleVariants({ size })}>{title}</h3>
          </div>
          <NoddiIcon name='ChevronRight' size={size === 'small' ? 'small' : 'medium'} />
        </div>
      </NoddiBasicCard>

      <NoddiDialog
        open={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        title={dialogTitle || title}
        maxWidth={dialogMaxWidth}
      >
        {children}
      </NoddiDialog>
    </div>
  );
};
